// Health Passport page — comprehensive patient health summary with risk signals and visit reports.
// Route: /patients/:id/health-passport

import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import {
  AiSummaryCard,
  RiskSummary,
  TrendTile,
  ReportRow,
  trendTiles,
  type HealthPassport,
} from "./HealthPassport.components.js";
import { CONSENT_LABELS } from "../lib/consent-labels.js";

export function HealthPassportPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery<HealthPassport>({
    queryKey: ["health-passport", id],
    queryFn: () => apiFetch<HealthPassport>(`/health-passport/${id}`),
  });

  if (isLoading) return <Shell><p className="p-8 text-sm text-slate-400" aria-busy="true">Loading…</p></Shell>;
  if (isError || !data) return <Shell><p className="p-8 text-sm text-red-600">Failed to load health passport.</p></Shell>;

  const { patient, recent_reports, consent, ai_summary } = data;
  const reports = recent_reports.slice(0, 5);

  return (
    <Shell>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(`/patients/${id}`)} className="text-sm text-navy-700 hover:underline">← Patient</button>
          <h1 className="text-xl font-semibold text-slate-900">Health Passport — {patient.full_name}</h1>
          <button onClick={() => window.print()} className="ml-auto text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 print:hidden">
            Download / Print PDF
          </button>
        </div>
        {!consent.p2 && (
          <div className="w-full bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-lg mb-4">
            {CONSENT_LABELS.P2} consent not granted — data access restricted
          </div>
        )}
        <AiSummaryCard summary={ai_summary} />
        <RiskSummary patient={patient} />
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Trend Signals</p>
          <div className="grid grid-cols-2 gap-3">
            {trendTiles(patient.trends).map((tile) => <TrendTile key={tile.label} label={tile.label} value={tile.value} />)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Recent Visit Reports</p>
          {reports.length === 0 ? <p className="text-sm text-slate-400">No reports found.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                    <th className="py-2 pr-3 font-medium">Date</th><th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium">BP</th><th className="py-2 pr-3 font-medium">HR</th>
                    <th className="py-2 pr-3 font-medium">SpO2</th><th className="py-2 pr-3 font-medium">Temp</th>
                    <th className="py-2 pr-3 font-medium">Weight</th><th className="py-2 font-medium">Escalation</th>
                  </tr>
                </thead>
                <tbody>{reports.map((r) => <ReportRow key={r._id} r={r} />)}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
